import { jwApi } from '@jwplayer/companion-gateway';
import { useCallback, useMemo } from 'react';

import { useQuery } from '/module/query';
import { useCurrentSiteIdOrThrow } from '/module/sites/hooks';

import type {
  ContentTypeSchemaDetails,
  ContentTypeSchemaMetadata,
  ContentTypeSchemaSection,
  ContentTypeSchemasListQueryParams,
} from './types';

export const getContentTypeFilters = ({
  contentTypeSchemas,
}: {
  contentTypeSchemas?: ContentTypeSchemaDetails[] | null;
}) => {
  const contentTypeFilters = contentTypeSchemas?.map((type) => {
    return {
      label: type?.metadata?.display_name,
      value: type?.metadata.name,
    };
  });
  return contentTypeFilters || [];
};

export const getContentTypeFilterList = ({
  contentTypeSchemas,
}: {
  contentTypeSchemas?: ContentTypeSchemaDetails[] | null;
}) => {
  const contentTypeFilters = contentTypeSchemas?.map((type) => type.metadata.name);
  return contentTypeFilters || [];
};

export const filterContentTypes = ({
  contentTypeSchemas,
  hostingTypes,
  isSeries,
  excludedHostingTypes,
}: {
  contentTypeSchemas?: ContentTypeSchemaDetails[] | null;
  hostingTypes?: string[] | undefined;
  isSeries?: boolean | undefined;
  excludedHostingTypes?: string[] | undefined;
}): ContentTypeSchemaDetails[] =>
  (contentTypeSchemas || [])
    .slice()
    .sort((a, b) => a.metadata.display_name.localeCompare(b.metadata.display_name))
    .filter(
      (el: ContentTypeSchemaDetails) =>
        el.metadata.is_active &&
        (hostingTypes === undefined || hostingTypes.includes(el.metadata.hosting_type)) &&
        (isSeries === undefined || isSeries === el.metadata.is_series) &&
        (excludedHostingTypes === undefined || !excludedHostingTypes.includes(el.metadata.hosting_type)),
    );

const createContentTypeSchema = async (siteId: string, metadata: ContentTypeSchemaMetadata) =>
  (await jwApi('content-type-schemas', 'create', { routeParams: { siteId }, input: { metadata } })).data;

const updateContentTypeSchema = async (siteId: string, schemaId: string, metadata: ContentTypeSchemaMetadata) =>
  (await jwApi('content-type-schemas', 'update', { routeParams: { siteId, schemaId }, input: { metadata } })).data;

const deleteContentTypeSchema = async (siteId: string, schemaId: string) =>
  (await jwApi('content-type-schemas', 'delete', { routeParams: { siteId, schemaId } })).data;

export const readContentTypeSchema = async (siteId: string, schemaId: string) =>
  (await jwApi('content-type-schemas', 'read', { routeParams: { siteId, schemaId } })).data;

export const listContentTypeSchemas = async (siteId: string, queryParams: ContentTypeSchemasListQueryParams) =>
  (await jwApi('content-type-schemas', 'list', { routeParams: { siteId }, queryParams })).data;

export const useContentTypeSchemas = {
  query: (schemaId?: string) => {
    const siteId = useCurrentSiteIdOrThrow();

    return useQuery(
      ['get-content-type-schema', schemaId],
      async () => {
        // The 'enabled' check in the query options below would prevent this error
        if (!schemaId) {
          throw new Error('Undefined: schemaId');
        }

        return readContentTypeSchema(siteId, schemaId);
      },
      {
        enabled: !!schemaId,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 3,
      },
    );
  },
  mutations: (siteId: string) => {
    const createSchema = useCallback(
      (metadata: ContentTypeSchemaMetadata) => createContentTypeSchema(siteId, metadata),
      [siteId],
    );

    const updateSchema = useCallback(
      (schemaId: string, metadata: ContentTypeSchemaMetadata) => updateContentTypeSchema(siteId, schemaId, metadata),
      [siteId],
    );

    const deleteSchema = useCallback((schemaId: string) => deleteContentTypeSchema(siteId, schemaId), [siteId]);

    return useMemo(
      () => ({
        createSchema: { mutateAsync: createSchema },
        updateSchema: { mutateAsync: updateSchema },
        deleteSchema: { mutateAsync: deleteSchema },
      }),
      [createSchema, updateSchema, deleteSchema],
    );
  },
  forSchema: (schemaId: string, siteId: string) => {
    const {
      createSchema: { mutateAsync: create_ },
      updateSchema: { mutateAsync: update_ },
      deleteSchema: { mutateAsync: delete_ },
    } = useContentTypeSchemas.mutations(siteId);

    const saveSchema = useCallback(
      (metadata: ContentTypeSchemaMetadata) => {
        if (!schemaId) {
          return create_(metadata);
        }

        return update_(schemaId, metadata);
      },
      [schemaId, create_, update_],
    );

    return useMemo(
      () => ({
        createSchema: { mutateAsync: create_ },
        updateSchema: { mutateAsync: update_.bind(this, schemaId) },
        saveSchema: { mutateAsync: saveSchema },
        deleteSchema: { mutateAsync: delete_.bind(this, schemaId) },
      }),
      [schemaId, create_, update_, saveSchema, delete_],
    );
  },
};

export const getContentTypeCustomParams = (
  contentType?: string,
  contentTypeFields?: ContentTypeSchemaSection[],
): Record<string, string> => {
  if (!contentType) {
    return {};
  }

  return (contentTypeFields || []).reduce(
    (acc: Record<string, string>, section) => {
      // Add default values for custom params when creating a media item
      section.fields.forEach((field) => {
        // Date and DateTime fields do not have default property -> check needed
        const defaultValue = 'default' in field.details ? field.details.default : undefined;

        if (defaultValue) {
          acc[field.param] = String(defaultValue);
        }
      });

      return acc;
    },
    { contentType },
  );
};
